import React, { useState, useEffect } from 'react'
import { Button, Radio, DatePicker, Popover } from 'antd'
import { SyncOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { setToStartOrEnd, getTimeAgoText } from './utils'

const { RangePicker } = DatePicker

/**
 * A self-contained time-range picker used across Usage Monitor pages.
 *
 * Props:
 *   filterTimeWindow  – array of { label, value } options; the option with
 *                       value === 'custom' triggers the date-range popover.
 *   defaultValue      – value of the initially selected filter option
 *                       (defaults to the first entry in filterTimeWindow).
 *   onFilterChange    – called with (startTime, endTime) whenever the active
 *                       time range changes.
 *   showSyncButton    – when true, renders the "last updated" sync button
 *                       alongside the filter (default: false).
 *   onSync            – callback invoked when the sync button is clicked.
 *   lastUpdatedTime   – Date instance used to compute the "X mins ago" label;
 *                       required when showSyncButton is true.
 */
const ReportTimePicker = ({
  filterTimeWindow,
  defaultValue,
  onFilterChange,
  showSyncButton = false,
  onSync,
  lastUpdatedTime,
}) => {
  const initialValue = defaultValue ?? filterTimeWindow[0].value
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(initialValue)
  const [startTimeFilter, setStartTimeFilter] = useState(null)
  const [endTimeFilter, setEndTimeFilter] = useState(null)
  const [openCustomDate, setOpenCustomDate] = useState(false)
  const [lastUpdatedText, setLastUpdatedText] = useState('Just now')

  // Recompute the "X mins ago" label periodically
  useEffect(() => {
    if (!showSyncButton || !lastUpdatedTime) return
    const id = setInterval(() => {
      setLastUpdatedText(getTimeAgoText(lastUpdatedTime))
    }, 300000)
    return () => clearInterval(id)
  }, [showSyncButton, lastUpdatedTime])

  // When lastUpdatedTime changes (i.e. a fresh fetch just happened), reset label
  useEffect(() => {
    if (showSyncButton) setLastUpdatedText('Just now')
  }, [lastUpdatedTime])

  // Derive start/end from the selected preset and notify the parent
  useEffect(() => {
    if (selectedTimeFilter === 'custom') return
    const endTime = dayjs()
    const hoursValue = Number(selectedTimeFilter)
    let startTime
    if (!isNaN(hoursValue)) {
      startTime = endTime.subtract(hoursValue, 'hour')
    } else if (selectedTimeFilter === '1d') {
      startTime = endTime.subtract(1, 'day')
    } else if (selectedTimeFilter === '1w') {
      startTime = endTime.subtract(7, 'day')
    }
    setStartTimeFilter(startTime)
    setEndTimeFilter(endTime)
    onFilterChange?.(startTime, endTime)
  }, [selectedTimeFilter])

  const handleDateChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) return
    const start = setToStartOrEnd(dayjs(dates[0]), true)
    const end = setToStartOrEnd(dayjs(dates[1]), false)
    setStartTimeFilter(start)
    setEndTimeFilter(end)
    onFilterChange?.(start, end)
    setOpenCustomDate(false)
  }

  return (
    <>
      <Radio.Group
        block
        optionType="button"
        buttonStyle="solid"
        value={selectedTimeFilter}
        onChange={(e) => {
          setStartTimeFilter(null)
          setEndTimeFilter(null)
          setSelectedTimeFilter(e.target.value)
          if (e.target.value !== 'custom') setOpenCustomDate(false)
        }}
      >
        {filterTimeWindow.map((timeValue) => {
          if (timeValue.value !== 'custom')
            return (
              <Radio.Button value={timeValue.value} key={timeValue.value}>
                {timeValue.label}
              </Radio.Button>
            )
          else
            return (
              <Popover
                key={timeValue.value}
                open={openCustomDate}
                trigger="click"
                placement="bottomRight"
                content={
                  <RangePicker
                    value={[startTimeFilter, endTimeFilter]}
                    style={{ width: '300px' }}
                    size="small"
                    onChange={handleDateChange}
                    maxDate={dayjs()}
                  />
                }
              >
                <Radio.Button
                  value={timeValue.value}
                  onClick={() => setOpenCustomDate(true)}
                >
                  {timeValue.label}
                </Radio.Button>
              </Popover>
            )
        })}
      </Radio.Group>
      {showSyncButton && (
        <Button onClick={onSync}>
          <SyncOutlined /> {lastUpdatedText}
        </Button>
      )}
    </>
  )
}

export default ReportTimePicker
